import * as React from 'react';
import {
    Link as RouterLink,
    useParams
} from 'react-router-dom';
import { useTranslation } from "react-i18next";

import {
    Avatar,
    Box,
    Divider,
    Link,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsIcon from '@mui/icons-material/Directions';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';

export default function LocationDetails({locations}) {
    let { id } = useParams();

    const { t } = useTranslation();

    const getLocation = (id) => {
        // eslint-disable-next-line
        const location = locations.filter(location => location.id == id)[0];

        return location;
    }

    const currentLocation = getLocation(id);

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    const dateTimeFormatter = new Intl.DateTimeFormat('de-DE', { timeStyle: 'short' });

    const getGoogleMapsUrl = (location) => {
        return `http://maps.google.com/maps?daddr=${currentLocation.address.street.replace(/\s/g, "+")}+${currentLocation.address.postcode.replace(/\s/g, "+")}+${currentLocation.address.city.replace(/\s/g, "city")}`;
    }

    const getFullName = (location) => {
        if (location.contact.lastname != null) {
            return  location.contact.firstname + ' ' + location.contact.lastname;
        }

        return location.franchisePartner.firstname + ' ' + location.franchisePartner.lastname;
    }

    const getFullImageUrl = (location) => {
        if (location.contact.imageUrl != null) {
            return BACKEND_URL + location.contact.imageUrl;
        } else if (location.franchisePartner.imageUrl) {
            return BACKEND_URL + location.franchisePartner.imageUrl;
        }

        return false;
    }

    // eslint-disable-next-line
    const getFaxUrl = (location) => {
        return `tel:${location.contact.faxNumber.replace(/\s/g, "")}`;
    }

    const getMailToUrl = (location) => {
        return `mailto:${location.contact.email}`;
    }

    // eslint-disable-next-line
    const getMobileUrl = (location) => {
        return `tel:${location.contact.mobileNumber.replace(/\s/g, "")}`;
    }

    const getPhoneUrl = (location) => {
        return `tel:${location.contact.phoneNumber.replace(/\s/g, "")}`;
    }

    const getWebsiteListItemButton = (location) => {
        if (location.websiteUrl == null) {
            return <></>;
        }

        return <ListItemButton component={Link} title={t("open_homepage")} href={location.websiteUrl.replace(/\s/g, "")}sx={{
            width: 24,
            justifyContent: "center"
        }}>
            <ListItemIcon sx={{justifyContent: "center"}}>
                <PublicIcon />
            </ListItemIcon>
        </ListItemButton>;
    }

    const getWebsiteListItem = (location) => {
        if (location.websiteUrl == null) {
            return <></>;
        }

        return <ListItem component={Link} title={t("open_homepage")} href={location.websiteUrl.replace(/\s/g, "")} underline="hover" color="#1eaf9b !important">
            <ListItemIcon>
                <PublicIcon />
            </ListItemIcon>
            <ListItemText
                id="location-details-list-website"
                primary={t("open_homepage")}
            />
        </ListItem>;
    }

    const getOpeningHours = (location) => {

        let openingHours = location.openingHours.map((openingHours) => (
            <ListItem
                key={`opening_hours_${openingHours.id}`}
                style={{padding:0}}
                secondaryAction={
                    <ListItemText primary={`${dateTimeFormatter.format(new Date(openingHours.opens))} - ${dateTimeFormatter.format(new Date(openingHours.closes))}`} />
                }>
                <ListItemText primary={`${t(openingHours.dayOfWeek)}:`} />
            </ListItem>
        ));

        let noticeHint = '';
        if (location.notice) {
            noticeHint = <ListItem><ListItemText>{location.notice}</ListItemText></ListItem>;
        }

        return <List dense={true}>{openingHours}{noticeHint}</List>;
    }

    return (
        <>
            <Box sx={{ flexGrow: 1, marginBottom: '20px' }}>
                <List sx={{ width: '100%' }}>
                    <ListItem disablePadding>
                        <ListItemButton component={RouterLink} to="/">
                            <ListItemIcon>
                                <ArrowBackIcon />
                            </ListItemIcon>
                            <ListItemText secondary={t("back_to_locations")} />
                        </ListItemButton>
                    </ListItem>
                    <ListItem sx={{
                        p: { sm: 2, xs: 0 }
                    }}>
                        <ListItemAvatar>
                            {
                                getFullImageUrl(currentLocation) ?
                                    <Avatar
                                        alt={getFullName(currentLocation)}
                                        src={getFullImageUrl(currentLocation)}
                                        sx={{
                                            width: { sm: 318, xs: 350 },
                                            height: { sm: 318, xs: 350 },
                                            p: { xs: 0 }
                                        }}
                                        // variant="rounded"
                                        variant="square"
                                    /> : <Avatar
                                        sx={{
                                            color: '#ffeb00',
                                            bgcolor: '#1eaf9b',
                                            width: { sm: 318, xs: 350 },
                                            height: { sm: 318, xs: 350 }
                                        }}
                                        // variant="rounded"
                                        variant="square"
                                    >
                                        <AccountCircleIcon />
                                    </Avatar>
                            }
                        </ListItemAvatar>
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            id="location-details-list-company-name"
                            primary={currentLocation.name}
                            secondary={`${getFullName(currentLocation)}`}
                         />
                    </ListItem>
                    <Divider />
                    <ListItem sx={{justifyContent: "center"}}>
                        <ListItemButton component={Link} title={t("open_route_planner")} href={getGoogleMapsUrl(currentLocation)} target="_blank" sx={{
                            width: 24,
                            justifyContent: "center"
                        }}>
                            <ListItemIcon sx={{justifyContent: "center"}}>
                                <DirectionsIcon />
                            </ListItemIcon>
                        </ListItemButton>
                        <ListItemButton component={Link} title={t("send_an_email")} href={getMailToUrl(currentLocation)} sx={{
                            width: 24,
                            justifyContent: "center"
                        }}>
                            <ListItemIcon sx={{justifyContent: "center"}}>
                                <EmailIcon />
                            </ListItemIcon>
                        </ListItemButton>
                        <ListItemButton component={Link} title={t("call_phone_number")} href={getPhoneUrl(currentLocation)} sx={{
                            width: 24,
                            justifyContent: "center"
                        }}>
                            <ListItemIcon sx={{justifyContent: "center"}}>
                                <PhoneIcon />
                            </ListItemIcon>
                        </ListItemButton>
                        {getWebsiteListItemButton(currentLocation)}
                    </ListItem>
                    <Divider />
                    <ListItem component={Link} title={t("open_route_planner")} href={getGoogleMapsUrl(currentLocation)} target="_blank" underline="hover" color="#1eaf9b !important">
                        <ListItemIcon>
                            <PlaceIcon />
                        </ListItemIcon>
                        <ListItemText
                            id="location-details-list-place"
                            primary={`${currentLocation.address.street}\n${currentLocation.address.postcode} ${t(currentLocation.address.city)}`}
                            primaryTypographyProps={{ style: { whiteSpace: "pre-line" } }}
                            secondary={`${t(currentLocation.address.state)}, ${t(currentLocation.address.country.title)}`} />
                    </ListItem>
                    <ListItem component={Link} title={t("send_an_email")} href={getMailToUrl(currentLocation)} underline="hover" color="#1eaf9b !important">
                        <ListItemIcon>
                            <EmailIcon />
                        </ListItemIcon>
                        <ListItemText
                            id="location-details-list-email"
                            primary={currentLocation.contact.email} />
                    </ListItem>
                    <ListItem component={Link} title={t("call_phone_number")} href={getPhoneUrl(currentLocation)} underline="hover" color="#1eaf9b !important">
                        <ListItemIcon>
                            <PhoneIcon />
                        </ListItemIcon>
                        <ListItemText
                            id="location-details-list-phone"
                            primary={currentLocation.contact.phoneNumber} />
                    </ListItem>
                    {getWebsiteListItem(currentLocation)}
                    <ListItem>
                        <ListItemIcon>
                            <AccessTimeIcon />
                        </ListItemIcon>
                        <ListItemText id="location-details-list-access-time" primary={getOpeningHours(currentLocation)} />
                    </ListItem>
                </List>
            </Box>
        </>
    );
}